import { useState, useEffect } from 'react';
import { AddCardModal } from './AddCardModal';
import { getPaymentMethods, retrySalePayment } from '../api/client';
import { colors } from '../constants/colors';
import { X, CreditCard, Loader2, Plus, Check, CheckCircle, AlertCircle } from 'lucide-react';
import type { PaymentMethod } from '../types';

interface RetryPaymentModalProps {
  saleId: string;
  itemName: string;
  amount: number;
  onClose: () => void;
  onSuccess: () => void;
}

function getCardBrandInfo(brand: string): { name: string; color: string } {
  const brands: Record<string, { name: string; color: string }> = {
    visa: { name: 'Visa', color: '#1A1F71' },
    mastercard: { name: 'Mastercard', color: '#EB001B' },
    amex: { name: 'American Express', color: '#006FCF' },
    discover: { name: 'Discover', color: '#FF6000' },
    diners: { name: 'Diners Club', color: '#0079BE' },
    jcb: { name: 'JCB', color: '#0B4EA2' },
    unionpay: { name: 'UnionPay', color: '#D10429' },
  };
  return brands[brand.toLowerCase()] || { name: brand, color: colors.gray[600] };
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

export function RetryPaymentModal({ saleId, itemName, amount, onClose, onSuccess }: RetryPaymentModalProps) {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRetrying, setIsRetrying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddCard, setShowAddCard] = useState(false);
  const [result, setResult] = useState<'success' | 'failed' | null>(null);
  const [failReason, setFailReason] = useState<string | null>(null);

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  const loadPaymentMethods = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getPaymentMethods();
      data.sort((a, b) => (a.isDefault === b.isDefault ? 0 : a.isDefault ? -1 : 1));
      setPaymentMethods(data);
      const defaultPm = data.find((pm) => pm.isDefault) || data[0];
      if (defaultPm) {
        setSelectedId(defaultPm.id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load payment methods');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = async () => {
    if (!selectedId) return;
    try {
      setIsRetrying(true);
      setError(null);
      setResult(null);
      const updated = await retrySalePayment(saleId, selectedId);
      if (updated.paymentStatus === 'FAILED') {
        setResult('failed');
        setFailReason(updated.paymentFailedReason || 'Your card was declined. Please try a different card.');
      } else {
        setResult('success');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsRetrying(false);
    }
  };

  const handleCardSaved = () => {
    setShowAddCard(false);
    loadPaymentMethods();
  };

  if (showAddCard) {
    return <AddCardModal onClose={() => setShowAddCard(false)} onSaved={handleCardSaved} />;
  }

  if (result === 'success') {
    return (
      <div style={styles.overlay} onClick={onSuccess}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={styles.resultContent}>
            <div style={styles.successIcon}>
              <CheckCircle size={32} color="#22C55E" />
            </div>
            <h2 style={styles.resultTitle}>Payment Authorized</h2>
            <p style={styles.resultSubtext}>{itemName} &middot; {formatCurrency(amount)}</p>
          </div>
          <div style={styles.footer}>
            <button style={styles.doneButton} onClick={onSuccess}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (result === 'failed') {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={styles.resultContent}>
            <div style={styles.failIcon}>
              <AlertCircle size={32} color="#DC2626" />
            </div>
            <h2 style={styles.resultTitle}>Payment Failed</h2>
            <p style={styles.resultSubtext}>{failReason}</p>
          </div>
          <div style={styles.footer}>
            <button style={styles.cancelButton} onClick={onClose}>
              Close
            </button>
            <button style={styles.tryAgainButton} onClick={() => setResult(null)}>
              Try Different Card
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={styles.headerIcon}>
            <CreditCard size={24} color="#DC2626" />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={styles.title}>Retry Payment</h2>
            <p style={styles.subtitle}>{itemName} &middot; {formatCurrency(amount)}</p>
          </div>
          <button style={styles.closeButton} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div style={styles.content}>
          {error && <div style={styles.error}>{error}</div>}

          {isLoading ? (
            <div style={styles.loadingState}>Loading payment methods...</div>
          ) : paymentMethods.length === 0 ? (
            <div style={styles.emptyState}>
              <p style={styles.emptyText}>No saved payment methods</p>
              <p style={styles.emptySubtext}>Add a card to retry this payment</p>
            </div>
          ) : (
            <div style={styles.cardList}>
              {paymentMethods.map((pm) => {
                const brandInfo = getCardBrandInfo(pm.cardBrand);
                const isSelected = selectedId === pm.id;
                const walletLabel = pm.walletType === 'apple_pay' ? 'Apple Pay'
                  : pm.walletType === 'google_pay' ? 'Google Pay' : null;
                return (
                  <button
                    key={pm.id}
                    style={{
                      ...styles.cardOption,
                      borderColor: isSelected ? colors.brand.blue : colors.gray[300],
                      backgroundColor: isSelected ? colors.brand.blue10 : colors.white,
                    }}
                    onClick={() => setSelectedId(pm.id)}
                  >
                    <div style={{
                      ...styles.radio,
                      borderColor: isSelected ? colors.brand.blue : colors.gray[400],
                      backgroundColor: isSelected ? colors.brand.blue : 'transparent',
                    }}>
                      {isSelected && <Check size={12} color={colors.white} />}
                    </div>
                    <div style={{ ...styles.cardIcon, backgroundColor: `${brandInfo.color}15` }}>
                      <CreditCard size={18} color={brandInfo.color} />
                    </div>
                    <div style={styles.cardInfo}>
                      <span style={styles.cardName}>
                        {walletLabel
                          ? `${walletLabel} (${brandInfo.name} ${pm.cardLast4})`
                          : `${brandInfo.name} ending in ${pm.cardLast4}`}
                        {pm.isDefault && <span style={styles.defaultBadge}>Default</span>}
                      </span>
                      <span style={styles.cardExpiry}>
                        Expires {pm.cardExpMonth.toString().padStart(2, '0')}/{pm.cardExpYear.toString().slice(-2)}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <button style={styles.addCardButton} onClick={() => setShowAddCard(true)}>
            <Plus size={18} />
            Add New Card
          </button>
        </div>

        <div style={styles.footer}>
          <button style={styles.cancelButton} onClick={onClose} disabled={isRetrying}>
            Cancel
          </button>
          <button
            style={{
              ...styles.retryButton,
              opacity: !selectedId || isRetrying ? 0.6 : 1,
            }}
            onClick={handleRetry}
            disabled={!selectedId || isRetrying}
          >
            {isRetrying ? (
              <>
                <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                Retrying...
              </>
            ) : (
              'Retry Payment'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    width: '100%',
    maxWidth: '480px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '20px 20px 0 20px',
  },
  headerIcon: {
    width: '48px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FEE2E2',
    borderRadius: '12px',
    flexShrink: 0,
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: colors.ink.black,
  },
  subtitle: {
    margin: '4px 0 0 0',
    fontSize: '14px',
    color: colors.gray[600],
  },
  closeButton: {
    marginLeft: 'auto',
    padding: '8px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: colors.gray[400],
  },
  content: {
    padding: '20px',
  },
  error: {
    padding: '12px',
    backgroundColor: '#FEE2E2',
    color: '#DC2626',
    borderRadius: '8px',
    fontSize: '14px',
    marginBottom: '16px',
  },
  loadingState: {
    textAlign: 'center',
    padding: '24px',
    color: colors.gray[600],
    fontSize: '14px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '24px',
  },
  emptyText: {
    fontSize: '15px',
    fontWeight: 600,
    color: colors.ink.black,
    margin: '0 0 4px 0',
  },
  emptySubtext: {
    fontSize: '14px',
    color: colors.gray[600],
    margin: 0,
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '12px',
  },
  cardOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    borderRadius: '10px',
    border: '1.5px solid',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left' as const,
  },
  radio: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: '2px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  cardIcon: {
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    flexShrink: 0,
  },
  cardInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: 1,
    minWidth: 0,
  },
  cardName: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.ink.black,
  },
  defaultBadge: {
    fontSize: '11px',
    fontWeight: 600,
    color: colors.accent.purple,
    backgroundColor: '#EDE9FE',
    padding: '2px 8px',
    borderRadius: '100px',
  },
  cardExpiry: {
    fontSize: '13px',
    color: colors.gray[600],
  },
  addCardButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.brand.blue,
    backgroundColor: colors.brand.blue10,
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  footer: {
    display: 'flex',
    gap: '12px',
    padding: '0 20px 20px 20px',
  },
  cancelButton: {
    flex: 1,
    padding: '14px',
    fontSize: '15px',
    fontWeight: 600,
    color: colors.gray[700],
    backgroundColor: colors.gray[100],
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  retryButton: {
    flex: 1,
    padding: '14px',
    fontSize: '15px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: '#DC2626',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  resultContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '32px 20px 20px 20px',
  },
  successIcon: {
    width: '56px',
    height: '56px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#DCFCE7',
    borderRadius: '50%',
    marginBottom: '16px',
  },
  failIcon: {
    width: '56px',
    height: '56px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FEE2E2',
    borderRadius: '50%',
    marginBottom: '16px',
  },
  resultTitle: {
    margin: '0 0 8px 0',
    fontSize: '18px',
    fontWeight: 600,
    color: colors.ink.black,
  },
  resultSubtext: {
    margin: 0,
    fontSize: '14px',
    color: colors.gray[600],
    lineHeight: 1.5,
  },
  doneButton: {
    flex: 1,
    padding: '14px',
    fontSize: '15px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: '#22C55E',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  tryAgainButton: {
    flex: 1,
    padding: '14px',
    fontSize: '15px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: '#DC2626',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
};
